<template>
  <div
    v-if="object && Object.keys(object).length"
    :key="indexedId"
    class="p-object"
  >
    <!-- deaccessioned object alert -->
    <Alert
      v-if="object.isDeaccessioned"
      :active="object.isDeaccessioned"
      uid="deaccessioned-object"
      :title="deaccessionedAlertTitle"
      :content="deaccessionedAlertContent"
      :urgent="false"
      :dismissable="false"
    />
    <ObjectProfile
      :human-made-object="object"
      :indexed-id="indexedId"
      :manuscript-parts-hidden="!displayManuscriptPartsComponent"
    />

    <RelatedSearchResultsCarousel
      v-if="showPartsCarousel"
      ref="parts-carousel"
      v-bind="partsCarouselProps"
    >
      <template #image="{ item }">
        <ImageAndCaption v-if="!item.cta" v-bind="item" :heading-level="3">
          <template #afterMetadata>
            <DeaccessionedTag v-if="item.is_deaccessioned" />
          </template>
        </ImageAndCaption>
      </template>
    </RelatedSearchResultsCarousel>

    <RecommendedArtwork
      v-if="object && department != undefined && !object.isDeaccessioned"
      :indexed-id="indexedId"
      :human-made-object="object"
    />
    <RelatedMedia
      v-if="object?.relatedMedia && Object.keys(object.relatedMedia).length"
      :related-media="object?.relatedMedia"
    />
    <!-- metadata  -->
    <HumanMadeObjectMetadata
      id="full-artwork-details"
      :human-made-object="object"
      class="p-human-made-object__metadata"
    />
    <!-- tabbed object data -->
    <HumanMadeObjectTabs
      v-if="object?.hasTabData === true"
      :human-made-object="object"
    />
    <TechnicalData :metadata-fields="technicalMetadataFields" />
  </div>
</template>

<script>
// getty libraries
import {
  Alert,
  ImageAndCaption,
  RelatedSearchResultsCarousel,
  TechnicalData,
  scrollToHashMixin,
  useLocalization,
  useEventBus
} from "@thegetty-private/getty-ui";
// internal imports
import { hideDupeManuscriptImages } from "@/helpers/configHelper";
// internal component imports
import DeaccessionedTag from "@/components/atoms/deaccessionedTag/index.vue";
import HumanMadeObjectMetadata from "@/components/molecules/humanMadeObjectMetadata/index.vue";
import RecommendedArtwork from "@/components/molecules/recommendedArtwork/index.vue";
import HumanMadeObjectTabs from "@/components/organisms/humanMadeObjectTabs/index.vue";
import ObjectProfile from "@/components/organisms/objectProfile/index.vue";
import { useObjectStore } from "@/stores/object";
import { mapStores } from "pinia";
import { useHead } from "@unhead/vue";
import { ref, defineAsyncComponent } from "vue";

// prettier-ignore
const RelatedMedia = defineAsyncComponent(
  () => import("@/components/organisms/relatedMedia/index.vue")
);

const title = ref(null);
const metaInfo = ref(null);

export default {
  name: "HumanMadeObjectHome",
  components: {
    Alert,
    HumanMadeObjectMetadata,
    HumanMadeObjectTabs,
    ImageAndCaption,
    ObjectProfile,
    RelatedMedia,
    RecommendedArtwork,
    TechnicalData,
    RelatedSearchResultsCarousel,
    DeaccessionedTag
  },
  mixins: [scrollToHashMixin],
  inject: ["updatePageTitle"],
  props: {
    /**
     * the "path/uuid" id of the HumanMadeObject
     */
    indexedId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * a way to inject a static object into the page
     */
    storybookData: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    return {
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      deaccessionedAlertTitle: this.translatePart(
        "object_page.deaccessioned_alert_title"
      ),
      /**
       * Deaccessioned alert content
       */
      deaccessionedAlertContent: this.translatePart(
        "object_page.deaccessioned_alert_content"
      ),
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus(),
      /**
       * the timeout interval for checking for a hash to load to
       */
      intervalId: undefined,
      /**
       * the # of times to try to scroll
       */
      intervalCount: 20
    };
  },
  computed: {
    ...mapStores(useObjectStore),
    stickyBoxData() {
      return this.object?.stickyBoxData;
    },
    object() {
      if (this.storybookData) {
        return this.storybookData;
      }
      return this.objectStore.getObject(this.indexedId);
    },
    /**
     * Gets the object description
     *
     * @returns {string}
     */
    displayText() {
      return this.object?.objectProfile?.description;
    },
    /**
     * Whether or not this object is a Manuscript with parts.
     *
     * @returns {boolean}
     */
    isManuscriptWithParts() {
      return this.object?.isManuscriptWithParts === true;
    },
    /**
     * Whether or not to display the manuscript parts component for pages of manuscripts with children.
     * Checks for distinct images for the parts, the value of the feature flag, and if the object is a root.
     *
     * @returns {boolean}
     */
    displayManuscriptPartsComponent() {
      if (
        hideDupeManuscriptImages() &&
        !this.partsHaveDistinctImages &&
        !this.isRoot
      ) {
        return false;
      } else {
        return true;
      }
    },
    /**
     * Constructs the related search results carousel props from object in the store
     *
     * @returns {object} props for related parts carousel
     */
    partsCarouselProps() {
      const items =
        this.hasAllObjects && !this.isRoot ? this.allObjects : this.parts;
      const parentId = this.object.structure?.rootId;

      return {
        items: items,
        heading: this.partsCarouselHeading,
        total: items?.length,
        showCta: true,
        showCount: true,
        testIdSuffix: "parts",
        searchParams: { parent_id: parentId }
      };
    },
    /**
     * Whether or not this object has any of the required complex object data
     *
     * @returns {boolean}
     */
    isComplexObject() {
      return this.object?.structure?.isComplexObject == true;
    },
    /**
     * the department the object is part of
     */
    department() {
      return this.object?.metadata?.department;
    },
    /**
     * Gets the page title
     *
     * @returns {string}
     */
    pageTitle() {
      return this.object?.objectProfile?.title;
    },
    /**
     * Gets the parts data (if applicable/complex object)
     *
     * @returns {array}
     */
    parts() {
      return this.object?.structure?.parts;
    },
    /**
     * Whether the parts of this object have different images
     *
     * @returns {boolean}
     */
    partsHaveDistinctImages() {
      return this.object?.structure?.partsHaveDistinctImages;
    },
    /**
     * Heading for parts of set for a complex object
     * @returns {String}
     */
    partsCarouselHeading() {
      return this.object?.structure?.partsCarouselHeading;
    },
    /**
     * Gets the all_objects data (if applicable/complex object)
     *
     * @returns {array}
     */
    allObjects() {
      return this.object?.structure?.allObjects;
    },
    /**
     * Whether or not this object has complex object all_objects data
     *
     * @returns {boolean}
     */
    hasAllObjects() {
      return this.object?.structure?.hasAllObjects;
    },
    /**
     * Gets the object's technical metadata
     *
     * @returns {string}
     * The metadata fields for the technical data section for the HumanMadeObject
     */
    technicalMetadataFields() {
      return this.object?.technicalMetadata;
    },
    /**
     * Gets the value of the is_root property in the elasticObjectData
     *
     * @returns {boolean} - whether the object is the root of a complex object
     */
    isRoot() {
      return this.object?.structure?.isRoot === true;
    },
    /**
     * Whether to display the parts carousel
     *
     * @returns {boolean}
     */
    showPartsCarousel() {
      return this.object?.structure?.showPartsCarousel === true;
    }
  },
  watch: {
    $route: "fetchData",
    stickyBoxData() {
      this.eventBus.emit("data-ready", this.stickyBoxData);
    }
  },
  async created() {
    await this.fetchData();
  },
  mounted() {
    useHead({
      title: title,
      meta: metaInfo
    });

    if (this.$route.hash != null && this.$route.hash != "") {
      // wait to make sure component loaded before attempting to scroll to a hash
      this.intervalId = setInterval(() => {
        if (document.querySelector(this.$route.hash) == undefined) {
          if (this.intervalCount < 1) {
            clearInterval(this.intervalId);
          }
          this.intervalCount--;
          return;
        }

        // calculate the sticky height to take it into account with the scrolling
        const stickyHeaderHeight = document.querySelector(
          ".o-hero-research-application__sticky-container"
        ).offsetHeight;
        this.scrollToHash(this.$route.hash, stickyHeaderHeight);

        clearInterval(this.intervalId);
      }, 500);
    }
  },

  methods: {
    /**
     * Gets all of the data necessary to fill out the object detail page
     * Requests all fo the data from the server
     * @async
     */
    async fetchData() {
      if (this.indexedId == undefined) {
        return;
      }
      // get the humanMadeObjectLOD
      await this.objectStore.addObject(this.indexedId);
      title.value = this.object?.head?.title;
      metaInfo.value = this.object?.head?.metadata;

      this.updatePageTitle(this.pageTitle);

      // check for metadata to send for analytics
      this.eventBus.emit("PageMetadata", {
        tmsId: this.object?.technicalMetadata?.other_ids?.tms_id?.name
      });
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
